import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Scale, Award, Users, Target, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';

const About = () => {
  const { attorney, firm } = mockData;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">About Us</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Committed to excellence, integrity, and unwavering advocacy for our clients.
            </p>
          </div>
        </div>
      </section>

      {/* Firm Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-navy-900 mb-6">Our Firm</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                {firm.name} is a boutique law firm dedicated to providing exceptional legal services 
                in Tax Law, Bankruptcy Law, and Personal Injury. We believe in a client-first approach, 
                combining legal expertise with personalized attention.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Our mission is simple: to reveal the path forward through complex legal challenges. 
                We stand by our clients every step of the way, ensuring they understand their options 
                and feel confident in their decisions.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                With a reputation built on trust, transparency, and results, we've helped countless 
                individuals and businesses overcome their legal obstacles and achieve peace of mind.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="border-2 border-slate-200">
                <CardContent className="p-6 text-center">
                  <Scale className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-navy-900 mb-2">Expert Legal Counsel</h3>
                  <p className="text-slate-600">Specialized knowledge in complex areas of law</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-slate-200">
                <CardContent className="p-6 text-center">
                  <Award className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-navy-900 mb-2">Proven Results</h3>
                  <p className="text-slate-600">Track record of successful outcomes</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-slate-200">
                <CardContent className="p-6 text-center">
                  <Users className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-navy-900 mb-2">Client-Centered</h3>
                  <p className="text-slate-600">Your needs drive our strategy</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-slate-200">
                <CardContent className="p-6 text-center">
                  <Target className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-navy-900 mb-2">Strategic Approach</h3>
                  <p className="text-slate-600">Tailored solutions for every case</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Attorney Profile */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">Meet Our Attorney</h2>
              <p className="text-xl text-slate-600">Experienced. Dedicated. Compassionate.</p>
            </div>
            
            <Card className="border-2 border-slate-200 shadow-xl">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                  <div className="lg:col-span-1 bg-navy-900 p-8 flex flex-col items-center justify-center text-white">
                    <div className="w-48 h-48 bg-slate-700 rounded-full mb-6 overflow-hidden">
                      <img 
                        src={attorney.image} 
                        alt={attorney.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{attorney.name}</h3>
                    <p className="text-slate-300 mb-6">{attorney.title}</p>
                    <Link to="/contact">
                      <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                        Schedule Consultation
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="lg:col-span-2 p-8 lg:p-12">
                    <h4 className="text-2xl font-bold text-navy-900 mb-6">Biography</h4>
                    <p className="text-slate-600 mb-8 leading-relaxed text-lg">
                      {attorney.bio}
                    </p>
                    
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-navy-900 mb-4">Education</h4>
                      <ul className="space-y-2">
                        {attorney.education.map((edu, index) => (
                          <li key={index} className="flex items-start">
                            <ArrowRight className="h-5 w-5 text-amber-600 mr-2 mt-1 flex-shrink-0" />
                            <span className="text-slate-600">{edu}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-xl font-bold text-navy-900 mb-4">Bar Admissions</h4>
                      <ul className="space-y-2">
                        {attorney.barAdmissions.map((bar, index) => (
                          <li key={index} className="flex items-start">
                            <ArrowRight className="h-5 w-5 text-amber-600 mr-2 mt-1 flex-shrink-0" />
                            <span className="text-slate-600">{bar}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-slate-600">The principles that guide our practice</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-amber-500">1</span>
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3">Integrity</h3>
              <p className="text-slate-600">We conduct ourselves with the highest ethical standards</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-amber-500">2</span>
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3">Excellence</h3>
              <p className="text-slate-600">We strive for outstanding results in every case</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-amber-500">3</span>
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3">Transparency</h3>
              <p className="text-slate-600">Clear communication and honest guidance throughout</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-amber-500">4</span>
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3">Compassion</h3>
              <p className="text-slate-600">We understand the human side of legal challenges</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Let Us Help You</h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Experience the difference that dedicated, personalized legal representation can make.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
              Get Started Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;